import { Link, useNavigate } from '@tanstack/react-router';
import { Briefcase, Gavel, MessageSquare, Plus, Timer } from 'lucide-react';
import { isFeatureOn } from '../../../lib/features';
import { useMemo, useState } from 'react';
import { z } from 'zod';
import { PROJECT_STATUSES, PROJECT_TYPES, type ProjectStatus } from '@buildbee/contracts';
import {
  Button,
  Checkbox,
  DataTable,
  Dialog,
  DialogContent,
  Form,
  FormActions,
  FormError,
  FormField,
  Input,
  PageHeader,
  Select,
  Textarea,
  formatDate,
  formatMoney,
  titleCase,
  useToast,
  useZodForm,
  type ColumnDef,
} from '@buildbee/ui';
import { AddressFields, addressErrors, cleanAddress, type AddressValue } from '../../../lib/address';
import { useRegional } from '../../../lib/regional';
import { errorMessage, trpc, type RouterOutputs } from '../../../lib/trpc';
import { MessagesLink } from '../chat';
import { StatusBadge } from '../components';
import { dollarsToCents } from '../lib';
import { ClockWidget } from '../time';

type Row = RouterOutputs['projects']['list']['items'][number];

const createSchema = z.object({
  name: z.string().trim().min(1, 'Required').max(200),
  type: z.enum(PROJECT_TYPES),
  status: z.enum(PROJECT_STATUSES),
  clientContactId: z.string(),
  pmId: z.string(),
  projectTemplateId: z.string(),
  startDate: z.string(),
  targetEndDate: z.string(),
  contractValue: z.string().refine((v) => v.trim() === '' || dollarsToCents(v) != null, 'Enter an amount'),
  description: z.string().max(10_000),
});

function CreateProjectDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const toast = useToast();
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const people = trpc.projects.people.useQuery({}, { enabled: open });
  const templates = trpc.projects.templates.useQuery(undefined, { enabled: open });
  const create = trpc.projects.create.useMutation();
  const regional = useRegional();
  const [address, setAddress] = useState<AddressValue>({});
  const form = useZodForm(createSchema, {
    defaultValues: { name: '', type: 'other', status: 'planning', clientContactId: '', pmId: '', projectTemplateId: '', startDate: '', targetEndDate: '', contractValue: '', description: '' },
  });

  const onSubmit = async (v: z.output<typeof createSchema>) => {
    try {
      const p = await create.mutateAsync({
        name: v.name,
        type: v.type,
        status: v.status,
        pmId: v.pmId || null,
        projectTemplateId: v.projectTemplateId || null,
        address: cleanAddress(address, regional.country),
        startDate: v.startDate || null,
        targetEndDate: v.targetEndDate || null,
        contractValueCents: dollarsToCents(v.contractValue) ?? 0,
        description: v.description || null,
      });
      await utils.projects.list.invalidate();
      toast.success('Project created', `${p.number} ${p.name}`);
      onOpenChange(false);
      form.reset();
      setAddress({});
      void navigate({ to: '/projects/$projectId', params: { projectId: p.id } });
    } catch (err) {
      toast.error('Could not create the project', errorMessage(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title="New project" description="Projects hold the schedule, documents, field records and bids." size="lg">
        <Form form={form} onSubmit={onSubmit}>
          <FormField name="name" label="Project name" required>
            <Input autoFocus placeholder="Harbor View Lobby" />
          </FormField>
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField name="type" label="Type">
              <Select options={PROJECT_TYPES.map((t) => ({ value: t, label: titleCase(t) }))} />
            </FormField>
            <FormField name="status" label="Status">
              <Select options={PROJECT_STATUSES.map((t) => ({ value: t, label: titleCase(t) }))} />
            </FormField>
            <FormField name="pmId" label="Project manager">
              <Select options={[{ value: '', label: 'Unassigned' }, ...(people.data ?? []).map((u) => ({ value: u.userId, label: u.name }))]} />
            </FormField>
            <FormField name="projectTemplateId" label="Template" description="Creates schedule phases from the template">
              <Select options={[{ value: '', label: 'None' }, ...(templates.data ?? []).map((t) => ({ value: t.id, label: t.name }))]} />
            </FormField>
            <FormField name="startDate" label="Start date">
              <Input type="date" />
            </FormField>
            <FormField name="targetEndDate" label="Target completion">
              <Input type="date" />
            </FormField>
            <AddressFields className="sm:col-span-2" legend={null} value={address} onChange={setAddress} defaultCountry={regional.country} errors={addressErrors(create.error)} />
            <FormField name="contractValue" label="Contract value ($)">
              <Input inputMode="decimal" placeholder="0.00" />
            </FormField>
          </div>
          <FormField name="description" label="Description">
            <Textarea rows={3} />
          </FormField>
          <FormError message={create.error ? errorMessage(create.error) : null} />
          <FormActions>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" loading={create.isPending}>
              Create project
            </Button>
          </FormActions>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

export function ProjectsListPage() {
  const [status, setStatus] = useState<ProjectStatus | ''>('');
  const [mine, setMine] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();
  const current = trpc.workspace.current.useQuery();
  const list = trpc.projects.list.useInfiniteQuery(
    { limit: 100, status: status || undefined, mine, includeArchived },
    { getNextPageParam: (last) => last.nextCursor ?? undefined, initialCursor: null },
  );
  const rows = useMemo(() => list.data?.pages.flatMap((p) => p.items) ?? [], [list.data]);
  const role = current.data?.membership.role;
  const canCreate = role === 'owner' || role === 'admin' || role === 'pm' || role === 'sales';

  const columns = useMemo<ColumnDef<Row, unknown>[]>(
    () => [
      {
        accessorKey: 'number',
        header: 'No.',
        cell: ({ row }) => <span className="figures text-xs text-muted-foreground">{row.original.number}</span>,
      },
      {
        accessorKey: 'name',
        header: 'Project',
        cell: ({ row }) => (
          <div className="min-w-0">
            <Link to="/projects/$projectId" params={{ projectId: row.original.id }} className="font-medium hover:underline" onClick={(e) => e.stopPropagation()}>
              {row.original.name}
            </Link>
            <p className="truncate text-xs text-muted-foreground">{[row.original.address?.line1, row.original.address?.city].filter(Boolean).join(', ') || titleCase(row.original.type)}</p>
          </div>
        ),
      },
      { accessorKey: 'status', header: 'Status', cell: ({ row }) => <StatusBadge status={row.original.status} />, filterFn: 'equalsString' },
      { accessorKey: 'clientName', header: 'Client', cell: ({ row }) => row.original.clientName ?? row.original.companyName ?? '—' },
      { accessorKey: 'pmName', header: 'PM', cell: ({ row }) => row.original.pmName ?? '—' },
      { accessorKey: 'targetEndDate', header: 'Target', cell: ({ row }) => <span className="figures">{formatDate(row.original.targetEndDate)}</span> },
      {
        accessorKey: 'percentComplete',
        header: 'Progress',
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <div className="h-1.5 w-16 overflow-hidden rounded-full bg-muted" aria-hidden>
              <div className="h-full bg-foreground" style={{ width: `${row.original.percentComplete}%` }} />
            </div>
            <span className="text-xs figures text-muted-foreground">{row.original.percentComplete}%</span>
          </div>
        ),
      },
      { accessorKey: 'contractValueCents', header: 'Contract', cell: ({ row }) => <span className="figures">{formatMoney(row.original.contractValueCents)}</span> },
    ],
    [],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title="Projects"
        description="Every job in one place: schedule, documents, daily logs, photos and punch lists."
        actions={
          <>
            <MessagesLink icon={<MessageSquare aria-hidden />} />
            {role === 'sub' && isFeatureOn('bids.invitations') ? (
              <Button asChild variant="outline">
                <Link to="/projects/bids/invitations">
                  <Gavel aria-hidden /> Bid invitations
                </Link>
              </Button>
            ) : null}
            {role && role !== 'sub' && role !== 'client' ? (
              <>
                <ClockWidget compact />
                <Button asChild variant="outline">
                  <Link to="/projects/timesheets">
                    <Timer aria-hidden /> Timesheets
                  </Link>
                </Button>
              </>
            ) : null}
            {canCreate ? (
              <Button onClick={() => setCreating(true)}>
                <Plus aria-hidden /> New project
              </Button>
            ) : null}
          </>
        }
      />
      <DataTable
        columns={columns}
        data={rows}
        loading={list.isLoading}
        error={list.error ? errorMessage(list.error) : undefined}
        onRetry={() => void list.refetch()}
        searchPlaceholder="Search projects…"
        onRowClick={(r) => void navigate({ to: '/projects/$projectId', params: { projectId: r.id } })}
        getRowId={(r) => r.id}
        toolbar={
          <div className="flex flex-wrap items-center gap-3">
            <div className="w-40">
              <Select aria-label="Status" value={status} onChange={(e) => setStatus(e.target.value as ProjectStatus | '')} options={[{ value: '', label: 'All statuses' }, ...PROJECT_STATUSES.map((s) => ({ value: s, label: titleCase(s) }))]} />
            </div>
            <Checkbox label="My projects" checked={mine} onCheckedChange={(v) => setMine(v === true)} />
            <Checkbox label="Archived" checked={includeArchived} onCheckedChange={(v) => setIncludeArchived(v === true)} />
          </div>
        }
        empty={{
          icon: <Briefcase />,
          title: status || mine ? 'No matching projects' : 'No projects yet',
          description: canCreate ? 'Create a project, or win a deal in the CRM to convert it into one.' : 'Projects you are added to will appear here.',
          action: canCreate ? <Button onClick={() => setCreating(true)}>New project</Button> : undefined,
        }}
        footer={
          list.hasNextPage ? (
            <div className="flex justify-center p-3">
              <Button variant="outline" size="sm" loading={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
                Load more
              </Button>
            </div>
          ) : undefined
        }
      />
      <CreateProjectDialog open={creating} onOpenChange={setCreating} />
    </div>
  );
}
